import { useEffect, useRef } from "react";
import { Player } from "@remotion/player";
import {
  AbsoluteFill,
  Easing,
  interpolate,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

const ease = Easing.bezier(0.16, 1, 0.3, 1);

const terminalLines = [
  "$ pnpm build",
  "> portfolio@1.0.0 build",
  "✓ compiled React workspace",
  "$ docker ps --format table",
  "api       healthy     8080",
  "web       running     3002",
  "$ git status --short",
  "M  src/Components/Hero.jsx",
  "$ npm test -- --watch=false",
  "PASS components/hero.test.tsx",
  "$ aws cloudfront create-invalidation",
  "✓ invalidation queued",
  "$ curl /api/health",
  "{\"status\":\"ok\",\"latency\":\"24ms\"}",
];

const chips = [
  { text: "</>", left: 92, top: 120, color: "#f2c94c", delay: 0.15 },
  { text: "{ }", left: 560, top: 158, color: "#2f6f88", delay: 0.32 },
  { text: "API", left: 96, top: 500, color: "#507a3a", delay: 0.5 },
  { text: "AI", left: 548, top: 492, color: "#d68a1a", delay: 0.68 },
];

function reveal(frame, fps, delay, duration = 0.55) {
  return interpolate(frame, [delay * fps, (delay + duration) * fps], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: ease,
  });
}

function CodingHeroScene() {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const float = interpolate(frame % (3 * fps), [0, 1.5 * fps, 3 * fps], [0, -12, 0], {
    easing: ease,
  });
  const cursor = interpolate(frame % fps, [0, 0.48 * fps, 0.5 * fps, fps], [1, 1, 0, 0], {
    extrapolateRight: "clamp",
  });
  const scan = interpolate(frame % (3.2 * fps), [0, 3.2 * fps], [-180, 620], {
    extrapolateRight: "extend",
  });
  const pulse = interpolate(frame % (2.4 * fps), [0, 1.2 * fps, 2.4 * fps], [0.9, 1, 0.9], {
    easing: ease,
  });
  const terminalOffset = interpolate(frame % (8 * fps), [0, 8 * fps], [0, -252], {
    extrapolateRight: "extend",
  });

  return (
    <AbsoluteFill
      style={{
        background: "transparent",
        fontFamily: "Plus Jakarta Sans, Arial, sans-serif",
        overflow: "hidden",
      }}
    >
      <svg viewBox="0 0 720 720" style={{ position: "absolute", inset: 0 }}>
        <defs>
          <radialGradient id="codingAura" cx="50%" cy="48%" r="52%">
            <stop offset="0%" stopColor="#f2c94c" stopOpacity="0.34" />
            <stop offset="62%" stopColor="#f2c94c" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#f2c94c" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="laptopGold" x1="92" y1="96" x2="628" y2="542">
            <stop offset="0%" stopColor="#fff8df" />
            <stop offset="48%" stopColor="#f2c94c" />
            <stop offset="100%" stopColor="#9f6500" />
          </linearGradient>
          <linearGradient id="screenDark" x1="130" y1="128" x2="590" y2="440">
            <stop offset="0%" stopColor="#302716" />
            <stop offset="100%" stopColor="#15110a" />
          </linearGradient>
        </defs>

        <circle cx="360" cy="350" r="286" fill="url(#codingAura)" opacity="0.95" />

        <g transform={`translate(0 ${float})`}>
          <rect
            x="104"
            y="96"
            width="512"
            height="356"
            rx="42"
            fill="url(#laptopGold)"
            filter="drop-shadow(0 34px 58px rgba(101, 78, 28, 0.28))"
          />
          <rect x="126" y="118" width="468" height="312" rx="30" fill="url(#screenDark)" />
          <rect
            x={scan}
            y="118"
            width="120"
            height="312"
            fill="rgba(255, 248, 223, 0.08)"
            transform="skewX(-18)"
          />

          <circle cx="164" cy="154" r="8" fill="#f2c94c" />
          <circle cx="190" cy="154" r="8" fill="#8fc6ab" />
          <circle cx="216" cy="154" r="8" fill="#d89b6a" />

          <foreignObject x="146" y="168" width="420" height="198">
            <div
              style={{
                height: 198,
                overflow: "hidden",
                fontFamily: "IBM Plex Mono, monospace",
                fontSize: 18,
                lineHeight: "25px",
                color: "#fff8df",
              }}
            >
              <div style={{ transform: `translateY(${terminalOffset}px)` }}>
                {[...terminalLines, ...terminalLines].map((line, index) => {
                  const isCommand = line.startsWith("$");
                  const isSuccess = line.startsWith("✓") || line.startsWith("PASS");
                  return (
                    <div
                      key={`${line}-${index}`}
                      style={{
                        whiteSpace: "nowrap",
                        color: isCommand ? "#f2c94c" : isSuccess ? "#8fc6ab" : "rgba(255, 248, 223, 0.72)",
                      }}
                    >
                      {line}
                    </div>
                  );
                })}
              </div>
            </div>
          </foreignObject>

          <rect x="518" y="340" width="12" height="28" rx="6" fill="#f2c94c" opacity={cursor} />

          <path d="M70 462 H650 L586 548 H134 Z" fill="#c98516" />
          <path d="M112 462 H608 L566 520 H154 Z" fill="#f2c94c" opacity="0.86" />
          <path d="M270 484 H450" stroke="rgba(43, 36, 22, 0.36)" strokeWidth="14" strokeLinecap="round" />

          <g transform={`scale(${pulse})`} style={{ transformOrigin: "360px 584px" }}>
            <rect x="224" y="574" width="272" height="52" rx="26" fill="rgba(255, 253, 244, 0.82)" />
            <rect x="252" y="592" width="82" height="12" rx="6" fill="#f2c94c" />
            <rect x="350" y="592" width="52" height="12" rx="6" fill="#2f6f88" />
            <rect x="418" y="592" width="50" height="12" rx="6" fill="#507a3a" />
          </g>
        </g>
      </svg>

      {chips.map((chip, index) => {
        const amount = reveal(frame, fps, chip.delay, 0.7);
        const y = Math.sin((frame / fps + index) * 1.6) * 9;
        return (
          <div
            key={chip.text}
            style={{
              position: "absolute",
              left: chip.left,
              top: chip.top + y,
              minWidth: 64,
              height: 50,
              padding: "0 14px",
              borderRadius: 16,
              display: "grid",
              placeItems: "center",
              background: "rgba(255, 253, 244, 0.84)",
              border: `1.5px solid ${chip.color}`,
              color: chip.color,
              fontFamily: "IBM Plex Mono, monospace",
              fontSize: 15,
              fontWeight: 700,
              boxShadow: "0 18px 45px rgba(101, 78, 28, 0.16)",
              opacity: amount,
              transform: `translateY(${(1 - amount) * 20}px) scale(${0.86 + amount * 0.14})`,
            }}
          >
            {chip.text}
          </div>
        );
      })}
    </AbsoluteFill>
  );
}

export function EngineeringMotion() {
  const playerRef = useRef(null);

  useEffect(() => {
    const play = () => {
      const player = playerRef.current;
      if (!player) return;
      player.mute?.();
      player.play?.();
    };

    play();
    const id = window.setInterval(() => {
      if (!playerRef.current?.isPlaying?.()) {
        play();
      }
    }, 1000);

    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="hero__motion-player">
      <Player
        ref={playerRef}
        component={CodingHeroScene}
        durationInFrames={240}
        fps={30}
        compositionWidth={720}
        compositionHeight={720}
        autoPlay
        loop
        muted
        controls={false}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}
